/**
 * Pure helpers of the messaging functions (no Deno or network code, so the
 * core tests can check them): phone numbers for SMS and the branded e-mail.
 */

/**
 * International form without "+" (what SMS providers want), or null if it does
 * not look like a phone number. Hungarian habits are understood: 06 30 123 4567,
 * +36 30 123 4567, 0036..., 30/123-4567 (a national number of the default country).
 */
export function normalizePhone(raw: string, defaultCountryCode = "36"): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) digits = digits.slice(1);
  else if (digits.startsWith("00")) digits = digits.slice(2);
  else if (defaultCountryCode === "36" && digits.startsWith("06")) digits = "36" + digits.slice(2);
  else if (digits.startsWith("0")) digits = defaultCountryCode + digits.slice(1);
  else if (!digits.startsWith(defaultCountryCode)) digits = defaultCountryCode + digits;
  if (/\D/.test(digits)) return null;
  return digits.length >= 10 && digits.length <= 15 ? digits : null;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Escaped text with line breaks and clickable https links. */
function textToHtml(text: string): string {
  return escapeHtml(text)
    .replace(/https:\/\/[^\s<]+/g, (u) => `<a href="${u}" style="color: #0A7D68; word-break: break-all;">${u}</a>`)
    .replace(/\r?\n/g, "<br>");
}

/** Logo images in e-mails: only a https link (the signed storage URL), never anything else. */
export function safeLogoUrl(url: string | null | undefined): string | null {
  return typeof url === "string" && /^https:\/\/[^\s"'<>]+$/.test(url) ? url : null;
}

/**
 * The header of every customer e-mail. Without a logo: the workshop's name on the dark band (as before).
 * With the workshop's own logo (owner, 2026-11-03): a white band, the logo on the left (at most 56 px high,
 * any logo shows on white) and the workshop's name beside it; the alt text is the name, so a mail program
 * that blocks images still shows who wrote.
 */
export function emailHeaderHtml(workshopName: string, logoUrl?: string | null): string {
  const name = escapeHtml(workshopName);
  const url = safeLogoUrl(logoUrl);
  if (!url) return `<div style="background: #0E2433; padding: 16px 24px; font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-size: 24px; font-weight: bold; letter-spacing: 0.5px; color: #ffffff;">${name}</div>`;
  return `<div style="background: #ffffff; padding: 16px 24px; border-bottom: 3px solid #0E2433;"><table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse: collapse;"><tr>`
    + `<td style="padding: 0 14px 0 0; vertical-align: middle;"><img src="${escapeHtml(url)}" alt="${name}" height="56" style="display: block; height: 56px; width: auto; max-width: 200px; border: 0;"></td>`
    + `<td style="vertical-align: middle; font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-size: 24px; font-weight: bold; letter-spacing: 0.5px; color: #0E2433;">${name}</td>`
    + `</tr></table></div>`;
}

/**
 * The quote e-mail in the Garagevo brand: the workshop's name first (dark band),
 * the text written by the program in the workshop's language, a button to the link.
 */
export function quoteEmailHtml(p: { workshopName: string; text: string; link: string; buttonLabel: string; logoUrl?: string | null }): string {
  return `<!doctype html><html><body style="margin: 0; background: #F2F5F7;">
<div style="background: #F2F5F7; padding: 24px 12px; font-family: 'Atkinson Hyperlegible Next', Verdana, Arial, Helvetica, sans-serif; font-size: 16px; line-height: 1.55; color: #0E2433;">
<div style="max-width: 560px; margin: 0 auto; background: #ffffff; border: 2px solid #0E2433; border-radius: 0; overflow: hidden;">
${emailHeaderHtml(p.workshopName, p.logoUrl)}
<div style="padding: 24px;">
<p style="margin: 0 0 18px;">${textToHtml(p.text)}</p>
<p style="margin: 22px 0;"><a href="${escapeHtml(p.link)}" style="display: inline-block; background: #0A7D68; color: #ffffff; font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-weight: bold; font-size: 18px; letter-spacing: 1px; text-transform: uppercase; padding: 14px 24px; border-radius: 0; text-decoration: none;">${escapeHtml(p.buttonLabel)}</a></p>
</div>
<div style="border-top: 1px solid #DCE3E8; padding: 14px 24px; font-size: 12px; color: #3D4E5B;"><strong>garag<span style="color: #0A7D68;">evo</span></strong></div>
</div>
</div>
</body></html>`;
}

/** A plain notice e-mail in the same brand frame (no button), e.g. the declined work reminder. */
export function noticeEmailHtml(p: { workshopName: string; text: string; logoUrl?: string | null }): string {
  return `<!doctype html><html><body style="margin: 0; background: #F2F5F7;">
<div style="background: #F2F5F7; padding: 24px 12px; font-family: 'Atkinson Hyperlegible Next', Verdana, Arial, Helvetica, sans-serif; font-size: 16px; line-height: 1.55; color: #0E2433;">
<div style="max-width: 560px; margin: 0 auto; background: #ffffff; border: 2px solid #0E2433; border-radius: 0; overflow: hidden;">
${emailHeaderHtml(p.workshopName, p.logoUrl)}
<div style="padding: 24px;">
<p style="margin: 0;">${textToHtml(p.text)}</p>
</div>
<div style="border-top: 1px solid #DCE3E8; padding: 14px 24px; font-size: 12px; color: #3D4E5B;"><strong>garag<span style="color: #0A7D68;">evo</span></strong></div>
</div>
</div>
</body></html>`;
}

/** The text and the link must belong together: the link is the workshop's own quote link. */
export function linkMatchesToken(link: string, token: string): boolean {
  return /^https:\/\/[^\s]+#quote\/[0-9a-f]{64}$/.test(link) && link.endsWith(`#quote/${token}`);
}

/** The same for the customer's intake link (#intake/<token>, feature "intakePhotos"). */
export function intakeLinkMatchesToken(link: string, token: string): boolean {
  return /^https:\/\/[^\s]+#intake\/[0-9a-f]{64}$/.test(link) && link.endsWith(`#intake/${token}`);
}

/** How long the photo and signature links of the customer's intake page work: one hour (the page asks again when opened). */
export const INTAKE_IMAGE_SECONDS = 3600;

/**
 * Storage path of the workshop's logo from its settings (settings.logo.path), or null. Same rule as
 * cleanLogo in packages/core/src/logo.ts and public.is_logo_path in the database.
 */
export function logoPathOf(settings: unknown, workshopId: string): string | null {
  const logo = (settings as { logo?: { path?: unknown } } | null)?.logo;
  const path = typeof logo?.path === "string" ? logo.path : "";
  const m = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/logo-[0-9a-z]{1,20}\.(png|jpg)$/.exec(path);
  return m && m[1] === workshopId ? path : null;
}

/** How long the logo link in an e-mail works: 30 days (customers often open a mail days later). */
export const EMAIL_LOGO_SECONDS = 30 * 24 * 3600;
/** How long the logo link of the quote page works: one hour (the page asks again when opened). */
export const QUOTE_LOGO_SECONDS = 3600;
